import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PlusIcon, SparklesIcon, ArrowUpRightIcon, LayersIcon, SearchIcon, CheckIcon, FolderIcon, Trash2Icon } from 'lucide-react';
import { RuleTarget, SellingRule, EMPTY_RULE, catKey, removeRule, saveRule, suggestionFor, targetPool, useSelling } from '../../data/sellingRules';

/* ------------------------------------------------------------------ shared */

const TYPE_LABEL: Record<RuleTarget['type'], string> = {
  category: 'Category',
  product: 'Product',
  service: 'Service'
};

/** A recommendation target, readable at a glance. */
export function TargetChip({
  target,
  onRemove
}: {
  target: RuleTarget;
  onRemove?: () => void;
}) {
  return <span className="inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1 rounded-full bg-white border border-slate-200 text-[12.5px] font-semibold text-dark-navy" data-id="element-1188" data-mp="3m2tbvpy3n3av">
      {target.type === 'category' && <FolderIcon className="w-3 h-3 text-slate-400" data-id="element-1189" data-mp="gk5ktdtno5ep" />}
      {target.label}
      {onRemove && <button onClick={onRemove} aria-label={`Remove ${target.label}`} className="w-4 h-4 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-error transition-colors duration-150 ease-out" data-id="element-1190" data-mp="ligrxgskg1t8">
          <XIcon className="w-3 h-3" data-id="element-1191" data-mp="uhc191qwps0r" />
        </button>}
    </span>;
}

/** One line summarising what a rule recommends — used wherever rules appear. */
export function RuleSummary({
  rule
}: {
  rule: SellingRule;
}) {
  const has = rule.better.length > 0 || rule.addons.length > 0;
  if (!has) return <p className="text-[13px] text-slate-400" data-id="element-1192" data-mp="1e7eazm4lq2n1">Nothing recommended yet</p>;
  return <div className="space-y-1.5" data-id="element-1193" data-mp="1i99b4nasq9z8">
      {rule.better.length > 0 && <p className="text-[13px] text-slate-600" data-id="element-1194" data-mp="1ib2h00dl4q66">
          <span className="font-semibold text-slate-400" data-id="element-1195" data-mp="1n7kjetlg0cfc">Better option: </span>
          {rule.better.map(b => b.label).join(' · ')}
        </p>}
      {rule.addons.length > 0 && <p className="text-[13px] text-slate-600" data-id="element-1196" data-mp="3faf58xooa4b1">
          <span className="font-semibold text-slate-400" data-id="element-1197" data-mp="3fbq6oqunazyz">Goes well with: </span>
          {rule.addons.map(a => a.label).join(' · ')}
        </p>}
    </div>;
}

/* ------------------------------------------------------------ rule editor */

/**
 * The one editor used at every level. It asks two questions and nothing else —
 * style and frequency belong to the shop, not repeated here.
 */
export function RuleEditor({
  rule,
  pool,
  onChange,
  strategyNote
}: {
  rule: SellingRule;
  pool: RuleTarget[];
  onChange: (rule: SellingRule) => void;
  strategyNote: string;
}) {
  const [picking, setPicking] = useState<'better' | 'addons' | null>(null);
  const chosen = [...rule.better, ...rule.addons].map(x => x.label.toLowerCase());
  const add = (slot: 'better' | 'addons', target: RuleTarget) => onChange({
    ...rule,
    [slot]: [...rule[slot], target]
  });
  const drop = (slot: 'better' | 'addons', label: string) => onChange({
    ...rule,
    [slot]: rule[slot].filter(x => x.label !== label)
  });
  return <div className="space-y-4" data-id="element-1198" data-mp="jbmoh1nug5l4">
      <Slot icon={ArrowUpRightIcon} label="Better option" tag="Upsell" help="A better, larger or higher-value choice." targets={rule.better} onRemove={l => drop('better', l)} onAdd={() => setPicking(picking === 'better' ? null : 'better')} open={picking === 'better'} pool={pool.filter(p => !chosen.includes(p.label.toLowerCase()))} onPick={x => {
      add('better', x);
      setPicking(null);
    }} data-id="element-1199" data-mp="jcfo7iodxutr" />
      <Slot icon={LayersIcon} label="Goes well with" tag="Cross-sell" help="Useful extras, services or related items." targets={rule.addons} onRemove={l => drop('addons', l)} onAdd={() => setPicking(picking === 'addons' ? null : 'addons')} open={picking === 'addons'} pool={pool.filter(p => !chosen.includes(p.label.toLowerCase()))} onPick={x => {
      add('addons', x);
      setPicking(null);
    }} data-id="element-1200" data-mp="4hhtwk9oeva1" />
      <p className="text-[12px] text-slate-400 leading-relaxed" data-id="element-1201" data-mp="106fhecmsz0ou">
        {strategyNote}
      </p>
    </div>;
}
function Slot({
  icon: Icon,
  label,
  tag,
  help,
  targets,
  onRemove,
  onAdd,
  open,
  pool,
  onPick
}: {
  icon: typeof LayersIcon;
  label: string;
  tag: string;
  help: string;
  targets: RuleTarget[];
  onRemove: (label: string) => void;
  onAdd: () => void;
  open: boolean;
  pool: RuleTarget[];
  onPick: (target: RuleTarget) => void;
}) {
  const [query, setQuery] = useState('');
  const list = useMemo(() => pool.filter(p => p.label.toLowerCase().includes(query.trim().toLowerCase())), [pool, query]);
  return <div className="rounded-2xl border border-slate-200 bg-white p-4" data-id="element-1202" data-mp="3eveaskc8j8e">
      <div className="flex items-start gap-2.5" data-id="element-1203" data-mp="2f2gt70zgps84">
        <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-1204" data-mp="2f41lejyb0z2t" />
        <div className="min-w-0" data-id="element-1205" data-mp="2j1pi9sjk51xc">
          <p className="flex items-center gap-2 flex-wrap" data-id="element-1206" data-mp="2j54y4xl22ezt">
            <span className="text-[11px] font-bold uppercase tracking-wider text-dark-navy" data-id="element-1207" data-mp="2o2wrzx59gb6n">
              {label}
            </span>
            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-500" data-id="element-1208" data-mp="2sz3z3t9e7sq6">
              {tag}
            </span>
          </p>
          <p className="text-[12.5px] text-slate-500 mt-1" data-id="element-1209" data-mp="20dkkd1pl0b6g">{help}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-3" data-id="element-1210" data-mp="25auh72favp4b">
        {targets.map(x => <TargetChip key={x.label} target={x} onRemove={() => onRemove(x.label)} data-id="element-1211" data-mp="2a3nljp6uwvfv" />)}
        <button onClick={onAdd} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed border-slate-300 text-[12.5px] font-bold text-slate-500 hover:border-ai-purple hover:text-ai-purple transition-colors duration-150 ease-out" data-id="element-1212" data-mp="1cl42ar5ugj75">
          <PlusIcon className="w-3 h-3" data-id="element-1213" data-mp="15hxjw2eoq2ys" />
          Add
        </button>
      </div>

      <AnimatePresence initial={false} data-id="element-1214" data-mp="1jawze4egvx6c">
        {open && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-1215" data-mp="1ebtuql2efsyw">
            <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-1216" data-mp="vnx8jist81hm">
              <div className="relative" data-id="element-1217" data-mp="2bysrevt3d0ws">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1218" data-mp="2c1x64n46nf1p" />
                <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search categories, products or services…" className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] placeholder:text-slate-400 outline-none focus:border-ai-purple transition-colors duration-150 ease-out" data-id="element-1219" data-mp="3oym6vmfzfslc" />
              </div>
              <div className="max-h-48 overflow-y-auto mt-2 space-y-1" data-id="element-1220" data-mp="ci0s19ebhwms">
                {list.length === 0 ? <p className="text-[13px] text-slate-400 py-4 text-center" data-id="element-1221" data-mp="gkx7wsch3en1">
                    Nothing else matches that.
                  </p> : list.map(p => <button key={`${p.type}-${p.label}`} onClick={() => {
              onPick(p);
              setQuery('');
            }} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-left hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-1222" data-mp="7o60xkwdufdo">
                      <span className="text-[13.5px] font-semibold text-dark-navy flex-1 truncate" data-id="element-1223" data-mp="2rhetn197kjbj">
                        {p.label}
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400 flex-shrink-0" data-id="element-1224" data-mp="2hmd3c2375b7d">
                        {TYPE_LABEL[p.type]}
                      </span>
                    </button>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}

/* --------------------------------------------------------------- the sheet */

interface Props {
  kind: string;
  categories: string[];
  counts: Record<string, number>;
  onClose: () => void;
  /** Opened focused on one category, from Manage categories or a product. */
  initialCategory?: string;
}

/**
 * Category-level recommendations — the primary place this gets configured.
 * A merchant sets a handful of rules here and an entire catalogue is covered.
 */
export function SellingRulesManager({
  kind,
  categories,
  counts,
  onClose,
  initialCategory
}: Props) {
  const {
    strategy,
    rules
  } = useSelling();
  const [editing, setEditing] = useState<string | null>(initialCategory ?? null);
  const [draft, setDraft] = useState<SellingRule>(EMPTY_RULE);
  const [suggesting, setSuggesting] = useState(false);
  const pool = useMemo(() => targetPool(kind, categories), [kind, categories]);
  const strategyNote = `Talab will raise these using your shop setting — ${strategy.style.toLowerCase()}, up to ${strategy.max} per conversation.`;
  const openEditor = (c: string) => {
    setEditing(c);
    setDraft(rules[catKey(c)] ?? EMPTY_RULE);
  };

  /* --- what Talab would propose for the categories still unconfigured --- */
  const proposals = useMemo(() => {
    const out: {
      category: string;
      rule: SellingRule;
    }[] = [];
    categories.forEach(c => {
      if (rules[catKey(c)] || out.length >= 5) return;
      const s = suggestionFor(kind, c);
      if (!s) return;
      out.push({
        category: c,
        rule: {
          better: s.better,
          addons: s.addons,
          active: true
        }
      });
    });
    return out;
  }, [categories, kind, rules]);
  const configured = categories.filter(c => rules[catKey(c)]).length;
  return <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-1225" data-mp="1aibxmifzbo0k">
      <div className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={onClose} data-id="element-1226" data-mp="1ag8y97sxsij6" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.18,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full max-w-2xl bg-white rounded-3xl shadow-large overflow-hidden max-h-[90vh] flex flex-col" data-id="element-1227" data-mp="1fbekk7q93z7s">
        <div className="flex items-start gap-4 px-7 pt-6 pb-5 border-b border-slate-100" data-id="element-1228" data-mp="39oqzvg4fvrlk">
          <div className="min-w-0 flex-1" data-id="element-1229" data-mp="34pxk1h1ojoi8">
            <h2 className="font-display font-bold text-lg text-dark-navy" data-id="element-1230" data-mp="34qpir0qkq2cq">
              Selling recommendations
            </h2>
            <p className="text-[13px] text-slate-500 mt-1 leading-relaxed" data-id="element-1231" data-mp="2ztfkcvl7igcr">
              Tell Talab what to suggest for groups of products. One rule covers
              every item in that category.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-1232" data-mp="3noi8t2g3mhsp">
            <XIcon className="w-4 h-4" data-id="element-1233" data-mp="f98bs8r2g2rs" />
          </button>
        </div>

        <div className="px-7 py-5 overflow-y-auto flex-1" data-id="element-1234" data-mp="f7x9yeh6l53b">
          {/* Talab does the bulk of the work — a whole catalogue in one pass */}
          {!editing && proposals.length > 0 && <div className="mb-5" data-id="element-1235" data-mp="2dhw2i2ljj5ia">
              {!suggesting ? <button onClick={() => setSuggesting(true)} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-ai-purple/30 bg-ai-purple/[0.03] text-left hover:bg-ai-purple/[0.06] transition-colors duration-150 ease-out" data-id="element-1236" data-mp="1zq7rk2n12n2w">
                  <SparklesIcon className="w-4 h-4 text-ai-purple flex-shrink-0" data-id="element-1237" data-mp="1ut8wnhvjic3n" />
                  <span className="min-w-0 flex-1" data-id="element-1238" data-mp="1gt6kmc9wycwb">
                    <span className="block text-[13.5px] font-bold text-ai-purple" data-id="element-1239" data-mp="1guhpkff3hqp0">
                      Suggest for me
                    </span>
                    <span className="block text-[12.5px] text-slate-500 mt-0.5" data-id="element-1240" data-mp="3b665ajt5hxag">
                      Talab can propose rules for {proposals.length}{' '}
                      {proposals.length === 1 ? 'category' : 'categories'}.
                    </span>
                  </span>
                </button> : <SuggestionReview proposals={proposals} onApprove={picked => {
            picked.forEach(p => saveRule(catKey(p.category), p.rule));
            setSuggesting(false);
          }} onDismiss={() => setSuggesting(false)} data-id="element-1241" data-mp="3lbxojbw5yk4u" />}
            </div>}

          {editing ? <div data-id="element-1242" data-mp="7uybsp8yr39i">
              <button onClick={() => setEditing(null)} className="text-[12.5px] font-bold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-1243" data-mp="7xkikmo15eqo">
                ← All categories
              </button>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-4" data-id="element-1244" data-mp="2nndtt1jy7b4c">
                When customers are interested in
              </p>
              <h3 className="font-display font-bold text-xl text-dark-navy mt-1" data-id="element-1245" data-mp="2l2pl18ulvy49">
                {editing}
                <span className="ml-2 text-[13px] font-semibold text-slate-400" data-id="element-1246" data-mp="2q0ho8z0pb6nr">
                  {counts[editing] ?? 0}{' '}
                  {(counts[editing] ?? 0) === 1 ? 'item' : 'items'}
                </span>
              </h3>

              <div className="mt-4" data-id="element-1247" data-mp="2z1fkgv4we9vs">
                <RuleEditor rule={draft} pool={pool.filter(p => p.label.toLowerCase() !== editing.toLowerCase())} onChange={setDraft} strategyNote={strategyNote} data-id="element-1248" data-mp="22bokwhhr1wj4" />
              </div>
            </div> : <div className="space-y-2" data-id="element-1249" data-mp="2g3v3ea8dkflq">
              {categories.length === 0 ? <p className="text-[13px] text-slate-400 py-10 text-center" data-id="element-1250" data-mp="1imo1f8tn6ez5">
                  Add some categories first — recommendations are set per
                  category.
                </p> : categories.map(c => {
            const rule = rules[catKey(c)];
            return <div key={c} className="flex items-start gap-4 p-4 rounded-2xl border border-slate-200" data-id="element-1251" data-mp="1b23mba84qygc">
                      <div className="min-w-0 flex-1" data-id="element-1252" data-mp="122qesn3kgjy9">
                        <p className="flex items-center gap-2 flex-wrap" data-id="element-1253" data-mp="x45c8h0udo6n">
                          <span className="text-sm font-bold text-dark-navy" data-id="element-1254" data-mp="x68atpg28pmz">
                            {c}
                          </span>
                          <span className="text-[12px] text-slate-400" data-id="element-1255" data-mp="ndu8auh5rezr">
                            {counts[c] ?? 0}{' '}
                            {(counts[c] ?? 0) === 1 ? 'item' : 'items'}
                          </span>
                          {rule && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${rule.active ? 'bg-success/10 text-success' : 'bg-slate-100 text-slate-500'}`} data-id="element-1256" data-mp="9eugxf29glzw">
                              {rule.active ? 'Active' : 'Paused'}
                            </span>}
                        </p>
                        <div className="mt-2" data-id="element-1257" data-mp="3qx9sgrt3kig9">
                          {rule ? <RuleSummary rule={rule} data-id="element-1258" data-mp="ihq3i4zglm70" /> : <p className="text-[13px] text-slate-400" data-id="element-1259" data-mp="ik2jiclnpmhf">
                              No recommendations — using shop defaults only.
                            </p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0" data-id="element-1260" data-mp="dpouqt1i2dlq">
                        {rule && <button onClick={() => removeRule(catKey(c))} aria-label={`Remove rule for ${c}`} className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-error transition-colors duration-150 ease-out" data-id="element-1261" data-mp="11f9g1il4lmy6">
                            <Trash2Icon className="w-3.5 h-3.5" data-id="element-1262" data-mp="2zoyjdlc9lk8i" />
                          </button>}
                        <button onClick={() => openEditor(c)} className="px-3.5 py-2 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-700 hover:border-ai-purple hover:text-ai-purple transition-colors duration-150 ease-out" data-id="element-1263" data-mp="2vp7w3rly5m8q">
                          {rule ? 'Edit' : 'Set up'}
                        </button>
                      </div>
                    </div>;
          })}
            </div>}
        </div>

        <div className="flex items-center justify-between gap-3 px-7 py-5 border-t border-slate-100" data-id="element-1264" data-mp="3ng3whsqtlyf4">
          <p className="text-[12px] text-slate-400 min-w-0" data-id="element-1265" data-mp="bcugl3tefomw">
            {editing ? 'Applies to every item in this category unless an item overrides it.' : `${configured} of ${categories.length} categories configured`}
          </p>
          {editing ? <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1266" data-mp="p3h6q7l3g67d">
              <button onClick={() => setEditing(null)} className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-1267" data-mp="p5tw35t6liug">
                Cancel
              </button>
              <button onClick={() => {
            saveRule(catKey(editing), draft);
            setEditing(null);
          }} className="px-5 py-2.5 bg-ai-purple text-white text-sm font-bold rounded-xl hover:bg-purple-600 transition-colors duration-150 ease-out" data-id="element-1268" data-mp="142b5vslq2pew">
                Save
              </button>
            </div> : <button onClick={onClose} className="px-5 py-2.5 bg-dark-navy text-white text-sm font-bold rounded-xl hover:bg-slate-800 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-1269" data-mp="32c0adyolksqt">
              Done
            </button>}
        </div>
      </motion.div>
    </div>;
}

/* ------------------------------------------------------------- suggestions */

function SuggestionReview({
  proposals,
  onApprove,
  onDismiss
}: {
  proposals: {
    category: string;
    rule: SellingRule;
  }[];
  onApprove: (picked: {
    category: string;
    rule: SellingRule;
  }[]) => void;
  onDismiss: () => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(proposals.map(p => p.category)));
  const toggle = (c: string) => setPicked(prev => {
    const next = new Set(prev);
    next.has(c) ? next.delete(c) : next.add(c);
    return next;
  });
  return <div className="rounded-2xl border border-ai-purple/30 bg-ai-purple/[0.03] p-4" data-id="element-1270" data-mp="1vwg1kqos4cd2">
      <p className="inline-flex items-center gap-2 text-sm font-bold text-ai-purple" data-id="element-1271" data-mp="w76ec4qv4205">
        <SparklesIcon className="w-4 h-4" data-id="element-1272" data-mp="rdloizzfaec1" />
        Talab suggests these rules
      </p>
      <div className="space-y-2 mt-3" data-id="element-1273" data-mp="nectlrsncaew">
        {proposals.map(p => {
        const on = picked.has(p.category);
        return <button key={p.category} onClick={() => toggle(p.category)} className={`w-full flex items-start gap-3 p-3 rounded-xl border bg-white text-left transition-colors duration-150 ease-out ${on ? 'border-ai-purple' : 'border-slate-200'}`} data-id="element-1274" data-mp="icng85hybknn">
              <span className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${on ? 'bg-ai-purple text-white' : 'border border-slate-300'}`} data-id="element-1275" data-mp="165vy632bjwj1">
                {on && <CheckIcon className="w-3.5 h-3.5" data-id="element-1276" data-mp="3lv71up64js9h" />}
              </span>
              <span className="min-w-0 flex-1" data-id="element-1277" data-mp="3lxa01whfkrpa">
                <span className="block text-[13.5px] font-bold text-dark-navy" data-id="element-1278" data-mp="3bs1olsactcv7">
                  {p.category}
                </span>
                <span className="block mt-1" data-id="element-1279" data-mp="3gqv1pjlqb1uu">
                  <RuleSummary rule={p.rule} data-id="element-1280" data-mp="3lppwzl0vuivu" />
                </span>
              </span>
            </button>;
      })}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4" data-id="element-1281" data-mp="3qgy4ucmkdw6w">
        <button disabled={picked.size === 0} onClick={() => onApprove(proposals.filter(p => picked.has(p.category)))} className="px-4 py-2.5 rounded-xl bg-ai-purple text-white text-[13px] font-bold hover:bg-purple-600 transition-colors duration-150 ease-out disabled:opacity-40" data-id="element-1282" data-mp="3ui04guke8a9e">
          {picked.size === proposals.length ? 'Approve all' : `Approve ${picked.size}`}
        </button>
        <button onClick={onDismiss} className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-1283" data-mp="2a49bf98jtseg">
          Dismiss
        </button>
        <p className="text-[12px] text-slate-500" data-id="element-1284" data-mp="222rwfr2gj6tc">
          You can edit any rule after approving.
        </p>
      </div>
    </div>;
}